import {
  BoxConfig,
  kDefaultBoxConfig,
} from '@euler/app/components/LicensePlateNoInput';
import { LicensePlateNoKeyboard } from '@euler/app/components/LicensePlateNoKeyboard';
import { Typography } from '@euler/app/components/typography';
import { OcrPreviewBottomActions } from '@euler/app/flows/common/components/OcrPreviewBottomActions';
import { VehicleServiceInfoView } from '@euler/app/flows/common/components/VehicleServiceInfoView';
import { useOcrResultServiceInfoFeature } from '@euler/app/flows/order/functions/useOcrResultServiceInfoFeature';
import { OrderExtra } from '@euler/app/flows/order/types';
import {
  LayoutProviderView,
  useContainerLayout,
} from '@euler/components/layout/LayoutProvider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AsyncState } from '@euler/functions/useFetchAsyncState';
import { LicensePlateNoRecognizeInfo } from '@euler/model/ocr/LicensePlateNoRecognizeInfo';
import { VehicleServiceInfo } from '@euler/services/vehicle-info.service';
import { AnimatePresence, MotiView } from 'moti';
import { memo, useCallback, useState } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Fill } from 'react-slot-fill';
import { BehaviorSubject } from 'rxjs';
import { useTheme } from 'styled-components';

const kBoxConfig: BoxConfig = {
  ...kDefaultBoxConfig,
  backgroundColor: '#f5f5f5',
};

export const LicensePlateNoRecognizeResultView = memo(
  ({
    result,
    lastResult,
    orderDetail$,
    onPlateNoChange,
    extraInfo,
    fetchExtraInfo,
    onConfirm,
  }: {
    result: LicensePlateNoRecognizeInfo;
    lastResult: string | undefined;
    orderDetail$: BehaviorSubject<OrderExtra>;
    extraInfo: AsyncState<VehicleServiceInfo | null>;
    fetchExtraInfo: (result: LicensePlateNoRecognizeInfo) => any;
    onPlateNoChange?: (value: string) => void;
    onConfirm?: (plateNo: string, serviceInfo?: VehicleServiceInfo) => void;
  }) => {
    const layout = useContainerLayout();
    const boxSize = (layout.width - 4 * 8 - 26) / 8;
    const insets = useSafeAreaInsets();
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const [isPlateNoKeyboardVisible, setIsPlateNoKeyboardVisible] =
      useState(false);
    const [plateNo, setPlateNo] = useState(result.number);
    if (!lastResult) {
      lastResult = result.number;
    }

    const onPlateInputIndexChange = useCallback(() => {
      if (isPlateNoKeyboardVisible) return;
      setIsPlateNoKeyboardVisible(true);
    }, [isPlateNoKeyboardVisible]);

    const onPlateKeyboardDonePress = useCallback(
      (value: string) => {
        if (value !== lastResult) {
          onPlateNoChange?.(value);
          setPlateNo(value);
          fetchExtraInfo({ ...result, number: value });
        }
        setIsPlateNoKeyboardVisible(false);
      },
      [fetchExtraInfo, lastResult, onPlateNoChange, result],
    );

    const onFetchExtraInfo = useCallback(() => {
      fetchExtraInfo(result);
    }, [fetchExtraInfo, result]);

    const serviceInfoFeature = useOcrResultServiceInfoFeature(
      orderDetail$,
      extraInfo.status === 'success' ? extraInfo.result : undefined,
    );

    return (
      <>
        <View
          css={`
            padding: ${rpx(8)}px ${rpx(15)}px 0;
          `}
        >
          <Typography.Text size={rpx(14)} lineHeight={rpx(16)} color="#999">
            识别结果，点击可手动修改
          </Typography.Text>
        </View>
        <LayoutProviderView>
          {({ width }) => (
            <LicensePlateNoKeyboard
              width={width}
              value={result.number}
              doneButtonText="确认"
              defaultSelectedIndex={-1}
              onDone={onPlateKeyboardDonePress}
              onInputIndexChange={onPlateInputIndexChange}
              css={`
                padding-bottom: ${insets.bottom}px;
                background-color: ${theme.keyboard.bgColor};
                box-shadow: 0 0 ${rpx(10)}px #ccc;
                elevation: 5;
              `}
            >
              {({ renderInput, renderKeypad }) => (
                <View
                  css={`
                    margin-top: ${rpx(15)}px;
                    padding: 0 ${rpx(15)}px;
                  `}
                >
                  {renderInput({ boxConfig: { ...kBoxConfig, size: boxSize } })}
                  <Fill name="ocr-preview-footer">
                    {isPlateNoKeyboardVisible && (
                      <AnimatePresence>
                        <MotiView
                          from={{ opacity: 0, translateY: 200 }}
                          animate={{ opacity: 1, translateY: 0 }}
                          exit={{ opacity: 0, translateY: 200 }}
                          transition={{ type: 'timing', duration: 200 }}
                          exitTransition={{ type: 'timing', duration: 200 }}
                          css={`
                            position: absolute;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            background-color: #d5d8dd;
                            padding-top: ${rpx(10)}px;
                            padding-bottom: ${insets.bottom}px;
                          `}
                        >
                          {renderKeypad()}
                        </MotiView>
                      </AnimatePresence>
                    )}
                  </Fill>
                </View>
              )}
            </LicensePlateNoKeyboard>
          )}
        </LayoutProviderView>

        <VehicleServiceInfoView
          state={extraInfo}
          onFetch={onFetchExtraInfo}
          {...serviceInfoFeature}
        />

        <Fill name="ocr-preview-footer">
          <OcrPreviewBottomActions
            visible={!isPlateNoKeyboardVisible}
            onConfirm={() => {
              onConfirm?.(
                plateNo,
                extraInfo.status === 'success'
                  ? extraInfo.result ?? undefined
                  : undefined,
              );
            }}
          />
        </Fill>
      </>
    );
  },
);
